# Cognitive Factory

<Section index="01" title="Overview">

<Lede>
  Two teams see the same drop in customer activation. One sees a release to
  reverse. The other also finds a planned experiment, an earlier failed attempt,
  and a changed product priority. Those records can change what the team should
  investigate—and whether changing code is useful at all.
</Lede>

The factory's ability to act depends on the world it can make sense of. A team
may execute an impeccable workflow while overlooking the reason the work exists.

<Callout>
  A cognitive factory connects present signals, remembered experience, and
  anticipated consequences so people and agents can develop better judgments.
</Callout>

<ArticleLink slug="the-knowledge-factory">The Knowledge Factory</ArticleLink>
develops triggers, agent DAGs, and the engineering of feedback loops.
This closing essay examines the capabilities those mechanisms serve:
how far the factory can see, what it can understand, and which experiences can
change its next decision.

</Section>

<Section index="02" title="The AI Factory Series">

1. <ArticleLink slug="vision-and-values">Vision and Values</ArticleLink>
2. <ArticleLink slug="understanding-and-bottlenecks">Understanding and Bottlenecks</ArticleLink>
3. <ArticleLink slug="truth-and-inference">Truth and Inference</ArticleLink>
4. <ArticleLink slug="the-knowledge-factory">The Knowledge Factory</ArticleLink>
5. <ArticleLink slug="the-ontology-factory">Ontology Factory</ArticleLink>
6. **Cognitive Factory — you are here**

</Section>

<Section index="03" title="1. How Far Can the Factory Make Sense?">

Michael Levin's cognitive light cone concerns the spatial and temporal reach
of the goals and events a system can measure, model, and try to affect. His
framework examines diverse forms of agency; applying it to an organization is
the adaptation proposed here. It supplies no validated intelligence score for
an AI factory. [Levin, 2019](https://doi.org/10.3389/fpsyg.2019.02688).

Consider a particular customer-onboarding workflow. Can it connect a failed
request to a customer's interrupted project? Can it retrieve the decision made
three months ago, distinguish it from today's priority, and anticipate a
tradeoff that will appear next month? Can the responsible team intervene and
later determine whether its explanation held?

These questions describe a capability profile. An agent that can edit many
files may have a narrow understanding of the consequences. A system that
retrieves a million documents may still miss the one decision that governs
the present case.

<Asset id="light-cone-scorecard" />

Use the profile to measure a configured factory against named tasks. Keep
observable reach, understanding, and authorized influence separate. More access
does not itself establish competence or grant permission to act.

</Section>

<Section index="04" title="2. Choose Signals That Explain Progress">

A factory needs a reason to attend to a signal. FranklinCovey's *The 4 Disciplines
of Execution* distinguishes lag measures of results from lead measures chosen
for their expected relationship to those results and the team's ability to
influence them. That gives us a practical question: which controllable behavior
do we expect to improve the outcome? [FranklinCovey](https://www.franklincovey.com/blog/5-ways-to-narrow-sales-focus-in-uncertainty/).

For the onboarding case, use an explicitly hypothetical measurement plan:

| Kind | Example | What the team needs to learn |
| --- | --- | --- |
| Lag measure | Share of a defined customer cohort completing its first successful import within seven days. | Did the intended customer result improve? |
| Candidate lead measure | Share of scheduled customer import trials completed with representative data before release. | Does this controllable practice predict fewer failed first imports? |
| Diagnostic signal | Validation errors, support reports, and where customers abandon the flow. | Which explanations fit the observed difficulty? |
| Guardrail | Support effort per onboarding and severe import failures. | Did apparent progress transfer cost or harm elsewhere? |

The proposed lead measure must earn its place through evidence. Counting trials
can reward superficial activity; an early-arriving metric is not automatically
a useful lead measure. Keep the definition, cohort, baseline, observation
window, owner, and expected relationship visible. If the practice improves
while the customer outcome does not, revisit the relationship.

A scoreboard becomes informative when it connects effort with consequence.
A threshold used to start work belongs to the factory's execution policy.
Cognition asks what the observation means, which competing explanations remain,
and whether the goal or measure needs revision.

</Section>

<Section index="05" title="3. A Second Brain Holds History and Current State">

Tiago Forte's *Building a Second Brain* offers a practice for keeping knowledge
outside immediate memory so it can support later work. His PARA method organizes
material around projects, areas of responsibility, resources, and archives.
Here, that is a starting point for organizational memory, extended with
ownership, record state, provenance, and relationships.
[Forte's Second Brain](https://www.buildingasecondbrain.com/) and
[PARA](https://fortelabs.com/blog/para/).

The factory needs both a history of what happened and a view of what currently
governs the work:

| Record | Current state | History that remains useful |
| --- | --- | --- |
| Project plan | Owner, objective, dependencies, status, and next decision. | Earlier assumptions, changed scope, and reasons for changes. |
| Experiment | Hypothesis, observation window, status, and interpretation. | Predicted and observed results, including failures and uncertainty. |
| Architecture decision record (ADR) | Accepted decision, scope, and whether it remains in force. | Alternatives, rationale, consequences, and superseding decisions. |
| Product and project priorities | Present order, constraints, owner, and effective date. | Which priorities changed, when, and why. |

A past priority is evidence about an earlier decision; it is not automatically
a current instruction. An experiment that failed in one customer segment does
not settle a different segment's case. A superseded ADR can explain the code
without authorizing a new implementation.

State therefore travels with the record. Preserve identities and revision
history, connect replacements to what they supersede, and give someone
responsibility for freshness. Conflicting records should expose a question
for the owner rather than quietly collapse into a confident summary.

</Section>

<Section index="06" title="4. Make Context Discoverable by Convention">

This memory can remain in the systems that maintain it: a project tracker,
research store, decision log, or document workspace. Copying their entire
contents into a source repository creates another body of material to keep
current.

The repository should keep the implementation, contracts, and decisions that
must be reviewed with the code. An ADR governing a code boundary may belong
beside that code. Changing roadmaps, raw research, and historical project
material can remain with their owners elsewhere.

Give people and agents a small, predictable entry point. A repository's
existing agent instructions or a linked context index can identify:

- the project and domain identifiers used to look up relevant records;
- the authoritative locations for plans, experiments, ADRs, and priorities;
- the owner, access method, and fields used to judge a record's state; and
- when each source should be consulted.

This is a proposed convention, not a new documentation repository. The entry
point routes discovery; it does not replicate the organization's memory.

At the destination, each record needs a stable identity, type, scope, owner,
status, effective or updated date, and links to evidence and related records.
Summaries help choose what to open. Source links let the reader recover the
reasoning and verify that the summary still applies.

</Section>

<Section index="07" title="5. Retrieve the Context the Decision Needs">

Forte's progressive summarization preserves access to underlying detail while
making the useful parts easier to find. We can adapt that principle into a
bounded retrieval practice for people and agents.
[Progressive Summarization](https://fortelabs.com/blog/progressive-summarization-a-practical-technique-for-designing-discoverable-notes/).

Start with the question and its scope, then navigate from a small index to
selected records and their evidence:

1. Identify the affected project, domain, decision, and time period.
2. Inspect record summaries and state to choose relevant sources.
3. Open the few records that can support or contradict the proposed explanation.
4. Follow their evidence or supersession links when a consequential detail is missing.
5. Assemble the relevant excerpts with provenance, dates, uncertainty, and a retrieval budget.

For the activation drop, the first packet might contain the current onboarding
priority, the release's experiment, one governing ADR, and the measurement
definition. An earlier experiment enters only if its conditions bear on a
live hypothesis. The team can expand its inquiry when those records reveal a
gap.

A retrieval limit needs a visible stopping condition. If missing information
could change the decision, mark the gap and seek it or escalate; do not treat
the budget as evidence that the search was complete. If access is unavailable,
preserve that uncertainty. Retrieved material informs the work within existing
authority; an archived instruction does not silently acquire new authority.

<Callout>
  Discoverability is a convention for finding relevant context. It is not a
  requirement to load the organization's entire memory into every prompt.
</Callout>

</Section>

<Section index="08" title="6. Experience Must Change the Next Judgment">

Return to the two teams. Finding the experiment may reveal that the activation
drop was an anticipated tradeoff. Finding its outcome may show that the tradeoff
failed. Finding a current priority may explain why the same tradeoff is now
unacceptable. The useful capability is relating those records to the present
decision.

<Asset id="consequence-returns-to-context-motif" />

Retain the comparison between expectation and result, the explanation it
changed, and who accepted the revision. Promote durable findings into the
appropriate definition, test, decision, or operating practice. Keep the fuller
experimental history linked and discoverable.

That division gives the factory a working memory for today's task and a
larger organizational memory it can consult. Neither requires every fact to
be permanently present in the model's context.

</Section>

<Section index="09" title="7. Evaluate the Factory's Cognitive Reach">

Use representative cases with known relevant evidence to test whether the
configured system can:

- find the current priority and distinguish it from a superseded one;
- connect a present symptom to an applicable earlier experiment or decision;
- preserve a credible alternative explanation and identify missing evidence;
- trace the claims in a recommendation to their sources; and
- revisit the recommendation when a later outcome contradicts it.

Record retrieval misses, stale-state mistakes, unsupported claims, and review
effort alongside task success. Compare the same tasks under different context
and memory arrangements. These are proposed evaluations of this factory's
sensemaking, not a universal cognition benchmark.

The temporal reach matters: how far back can the system recover applicable
experience, and how far forward can it formulate consequences that someone
will actually evaluate? Its reach across teams matters too: can one group's
learning inform another without erasing the difference between their contexts?
A larger capability profile is useful when it improves those judgments.

The series ends with an organization that can choose direction, test claims,
distribute understanding, execute work, maintain shared meaning, and make sense
of experience. Its second brain gives the next decision a history without
requiring every decision to carry the whole archive.

</Section>

<Section index="10" title="Sources">

- Michael Levin, [“The Computational Boundary of a ‘Self’: Developmental Bioelectricity Drives Multicellularity and Scale-Free Cognition”](https://doi.org/10.3389/fpsyg.2019.02688) (2019). The organizational capability profile is this article's adaptation.
- FranklinCovey, [“5 Ways to Narrow Your Sales Focus in Times of Uncertainty”](https://www.franklincovey.com/blog/5-ways-to-narrow-sales-focus-in-uncertainty/). Lead and lag measures; the onboarding measures are illustrative.
- Tiago Forte, [*Building a Second Brain*](https://www.buildingasecondbrain.com/).
- Tiago Forte, [“The PARA Method”](https://fortelabs.com/blog/para/). The record conventions proposed here extend a personal knowledge-management practice.
- Tiago Forte, [“Progressive Summarization: A Practical Technique for Designing Discoverable Notes”](https://fortelabs.com/blog/progressive-summarization-a-practical-technique-for-designing-discoverable-notes/). Selective agent retrieval is an adaptation, not an evaluated result of this source.
- W3C, [“PROV-O: The PROV Ontology”](https://www.w3.org/TR/prov-o/) (2013). A reference vocabulary for provenance; it does not prescribe this storage design.

</Section>
